// src/components/BacklogPage.js
// --- MODIFIED FILE ---
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { apiService } from '../services/api';
import { Loader2, ListChecks, CheckCircle, Plus } from 'lucide-react';
import { useAuth } from './Auth';
import BacklogTaskItem from './BacklogTaskItem';
import BacklogCalendarView from './BacklogCalendarView';
import { getMonday } from '../utils/dateUtils';
import { useNexusSync } from '../context/NexusSyncContext';
import { DEFAULT_PROJECT_PRIORITY_VALUE, getProjectPriorityValue } from '../utils/projectPriority';

const BacklogColumn = ({ title, tasks, onDrop, onUpdate, objectives, employees, projects, bgColorClass = 'bg-gray-100', onAddTask, status, onDeleteTask }) => {
    const [isOver, setIsOver] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [isAdding, setIsAdding] = useState(false);
    const [newTaskName, setNewTaskName] = useState("");
    const inputRef = React.useRef(null);

    useEffect(() => {
        if (isAdding && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isAdding]);

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsOver(true);
    };
    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsOver(false);
    };
    const handleDrop = (e) => {
        e.preventDefault();
        setIsOver(false);
        const task = JSON.parse(e.dataTransfer.getData('application/json'));
        onDrop(task);
    };

    const handleSaveNewTask = (e) => {
        e.preventDefault();
        if (newTaskName.trim()) {
            onAddTask(newTaskName.trim(), status);
            setNewTaskName("");
            setIsAdding(false);
        }
    };
    
    const handleCancel = () => {
        setNewTaskName("");
        setIsAdding(false);
    }

    return (
        <div 
            className={`flex-1 p-3 rounded-lg h-full flex flex-col ${bgColorClass} transition-colors ${isOver ? 'bg-blue-100' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <h2 className="text-lg font-bold mb-4 text-center text-gray-700">{title} ({tasks.length})</h2>
            <div className="flex-grow overflow-y-auto overscroll-contain space-y-2 pr-2 relative" style={{ scrollbarGutter: 'stable' }}>
                {tasks.length > 0 ? (
                    tasks.map(task => (
                        <BacklogTaskItem 
                            key={task.id} 
                            task={task} 
                            onUpdate={onUpdate} 
                            onDelete={onDeleteTask}
                            objectives={objectives} 
                            employees={employees} 
                            projects={projects}
                        />
                    ))
                ) : (
                    !isAdding && (
                        <div className={`absolute inset-0 flex items-center justify-center text-gray-400 italic pointer-events-none transition-opacity duration-200 ${isHovered && title !== 'Fait' ? 'opacity-0' : 'opacity-100'}`}>
                            {title === "Fait" ? (
                                <div className="flex flex-col items-center">
                                    <CheckCircle className="mx-auto mb-2 h-8 w-8 text-green-300"/>
                                    <span>Rien de terminé aujourd'hui</span>
                                </div>
                            ) : (
                                <span>Glissez une tâche ici</span>
                            )}
                        </div>
                    )
                )}

                <div className="mt-auto pt-2">
                    {isAdding ? (
                        <form onSubmit={handleSaveNewTask}>
                            <input
                                ref={inputRef}
                                type="text"
                                value={newTaskName}
                                onChange={(e) => setNewTaskName(e.target.value)}
                                onBlur={handleCancel}
                                onKeyDown={(e) => { if(e.key === 'Escape') handleCancel()}}
                                className="w-full border-b bg-transparent focus:outline-none focus:border-blue-500"
                                placeholder="Nom de la tâche..."
                            />
                        </form>
                    ) : (
                       isHovered && title !== "Fait" && (
                            <button 
                                onClick={() => setIsAdding(true)}
                                className="w-full flex items-center justify-center text-gray-400 hover:text-blue-500 transition-colors p-2 rounded-lg hover:bg-black/5"
                            >
                                <Plus size={16} className="mr-1" /> Ajouter une tâche
                            </button>
                        )
                    )}
                </div>
            </div>
        </div>
    );
};


const BacklogPage = () => {
    // ... (tous les états existants sont conservés)
    const { user } = useAuth();
    const [tasks, setTasks] = useState([]);
    const [objectives, setObjectives] = useState([]);
    const [projects, setProjects] = useState([]);
    const [allEmployees, setAllEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastSync, setLastSync] = useState(localStorage.getItem('lastTomorrowSync'));
    
    const calendarDates = useMemo(() => {
        const today = new Date();
        const startOfView = getMonday(today);
        return Array.from({ length: 14 }, (_, i) => {
            const date = new Date(startOfView);
            date.setDate(startOfView.getDate() + i);
            return date;
        });
    }, []);

    // Dates pour mobile: aujourd'hui + 2 prochains jours
    const mobileCalendarDates = useMemo(() => {
        const today = new Date();
        return [0, 1, 2].map(i => {
            const d = new Date(today);
            d.setDate(today.getDate() + i);
            return d;
        });
    }, []);

    const { lastMutation } = useNexusSync();
    const objectiveById = useMemo(() => {
        const map = new Map();
        objectives.forEach((objective) => {
            if (objective?.id !== undefined && objective?.id !== null) {
                map.set(String(objective.id), objective);
            }
        });
        return map;
    }, [objectives]);

    const projectById = useMemo(() => {
        const map = new Map();
        projects.forEach((project) => {
            if (project?.id !== undefined && project?.id !== null) {
                map.set(String(project.id), project);
            }
        });
        return map;
    }, [projects]);

    const fetchAllData = useCallback(async () => {
        if (!user) return;
        setLoading(true);
        setError(null);
        try {
            const [tasksData, projectsData, employeesData] = await Promise.all([
                apiService.getMyTasks(),
                apiService.get('/projects'),
                apiService.get('/employees')
            ]);
            
            setAllEmployees(employeesData);
            setProjects(projectsData);

            const objectivesPromises = projectsData.map(p => apiService.get(`/projects/${p.id}/objectives`));
            const objectivesByProject = await Promise.all(objectivesPromises);
            setObjectives(objectivesByProject.flat());

            const todayStr = new Date().toISOString().split('T')[0];
            const tomorrowDate = new Date();
            tomorrowDate.setDate(tomorrowDate.getDate() + 1);
            const tomorrowStr = tomorrowDate.toISOString().split('T')[0];

            if (lastSync !== todayStr) {
                const tasksToMoveToToday = tasksData.filter(t => t.planning_status === 'tomorrow');
                const tasksToMoveToTomorrow = tasksData.filter(t => t.due_date === tomorrowStr && t.planning_status !== 'today' && t.planning_status !== 'tomorrow');
                
                const updates = [];
                tasksToMoveToToday.forEach(t => updates.push(apiService.updateTask(t.id, { planning_status: 'today' })));
                tasksToMoveToTomorrow.forEach(t => updates.push(apiService.updateTask(t.id, { planning_status: 'tomorrow' })));

                if (updates.length > 0) {
                    await Promise.all(updates);
                    const refreshedTasks = await apiService.getMyTasks();
                    setTasks(refreshedTasks);
                } else {
                    setTasks(tasksData);
                }
                localStorage.setItem('lastTomorrowSync', todayStr);
                setLastSync(todayStr);
            } else {
                setTasks(tasksData);
            }

        } catch (err) {
            setError("Erreur de chargement des données.");
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [user, lastSync]);

    useEffect(() => {
        fetchAllData();
    }, [fetchAllData]);

    useEffect(() => {
        if (!lastMutation || !lastMutation.metadata) {
            return;
        }
        const entity = (lastMutation.metadata.entity || '').toLowerCase();
        if (entity === 'tasks' || entity === 'objectives') {
            fetchAllData();
        }
    }, [lastMutation, fetchAllData]);

    const handleUpdateTask = async (taskId, updateData) => {
        const task = tasks.find(t => t.id === taskId);
        if (!task) return;

        const finalUpdateData = { ...updateData };

        // Si la date d'échéance est modifiée pour demain
        if (finalUpdateData.due_date) {
            const tomorrowDate = new Date();
            tomorrowDate.setDate(tomorrowDate.getDate() + 1);
            const tomorrowStr = tomorrowDate.toISOString().split('T')[0];

            if (finalUpdateData.due_date === tomorrowStr && task.planning_status !== 'today' && task.planning_status !== 'tomorrow') {
                finalUpdateData.planning_status = 'tomorrow';
            }
        }

        const originalTasks = [...tasks];
        const updatedTask = { ...task, ...finalUpdateData };
        setTasks(prev => prev.map(t => t.id === taskId ? updatedTask : t));

        try {
            await apiService.updateTask(taskId, finalUpdateData);
        } catch(err) {
            setError(err.message || "Erreur mise à jour tâche.");
            setTasks(originalTasks);
        }
    };

    const handleDeleteTask = async (taskId) => {
        if (window.confirm("Êtes-vous sûr de vouloir supprimer cette tâche ?")) {
            const originalTasks = [...tasks];
            setTasks(prev => prev.filter(t => t.id !== taskId));
            try {
                await apiService.deleteTask(taskId);
            } catch (err) {
                setError(err.message || "Erreur de suppression de la tâche.");
                setTasks(originalTasks);
            }
        }
    };
    
    const handleAddTask = async (name, planningStatus) => {
        if(!user || !user.employee_id) {
            setError("Impossible de déterminer l'utilisateur pour créer la tâche.");
            return;
        }
        
        // 1. Créer une tâche "temporaire" pour l'affichage immédiat
        const tempId = `temp-${Date.now()}`;
        const tempTask = {
            id: tempId,
            name: name,
            planning_status: planningStatus,
            owner_id: user.employee_id,
            is_completed: false,
            created_at: new Date().toISOString(),
            // ... autres champs avec valeurs par défaut
            objective_id: null,
            objective_name: null,
            due_date: null,
            priority: null,
            updated_at: new Date().toISOString(),
        };

        // 2. Ajouter la tâche temporaire à l'état local
        setTasks(prevTasks => [...prevTasks, tempTask]);

        // 3. Préparer le payload pour l'API (sans l'ID temporaire)
        const payload = {
            name,
            planning_status: planningStatus,
            owner_id: user.employee_id,
            is_completed: false,
        };

        try {
            // 4. Appeler l'API pour créer la vraie tâche
            const createdTask = await apiService.createTask(payload);

            // 5. Remplacer la tâche temporaire par la vraie tâche (avec le bon ID)
            setTasks(prevTasks => prevTasks.map(t => t.id === tempId ? createdTask : t));

        } catch (err) {
            setError(err.message || "Erreur lors de la création de la tâche.");
            // 6. En cas d'erreur, retirer la tâche temporaire
            setTasks(prevTasks => prevTasks.filter(t => t.id !== tempId));
        }
    };
    const handleDrop = (task, newPlanningStatus) => {
        const updates = {};
        let needsUpdate = false;

        if (newPlanningStatus === 'done') {
            if (!task.is_completed) {
                updates.is_completed = true;
                needsUpdate = true;
            }
        } else if (task.is_completed) {
            updates.is_completed = false;
            updates.planning_status = newPlanningStatus;
            needsUpdate = true;
        } else if (task.planning_status !== newPlanningStatus) {
            updates.planning_status = newPlanningStatus;
            needsUpdate = true;
        }

        if (needsUpdate) {
            handleUpdateTask(task.id, updates);
        }
    };

    const handleDropOnDate = (task, date) => {
        const newDueDate = date.toISOString().split('T')[0];
        if (task.due_date !== newDueDate) {
            handleUpdateTask(task.id, { due_date: newDueDate });
        }
    };
    
    const sortedTasks = useMemo(() => {
        return [...tasks].sort((a, b) => {
            const priorityOrder = { urgent: 4, high: 3, medium: 2, low: 1 };
            const priorityA = priorityOrder[a.priority] || 0;
            const priorityB = priorityOrder[b.priority] || 0;
            if (priorityA !== priorityB) return priorityB - priorityA;
            const objectiveA = a.objective_id ? objectiveById.get(String(a.objective_id)) : null;
            const objectiveB = b.objective_id ? objectiveById.get(String(b.objective_id)) : null;
            const projectA = projectById.get(String(a.project_id ?? objectiveA?.project_id ?? ''));
            const projectB = projectById.get(String(b.project_id ?? objectiveB?.project_id ?? ''));
            const projectPriorityA = projectA ? getProjectPriorityValue(projectA, DEFAULT_PROJECT_PRIORITY_VALUE) : 8;
            const projectPriorityB = projectB ? getProjectPriorityValue(projectB, DEFAULT_PROJECT_PRIORITY_VALUE) : 8;
            if (projectPriorityA !== projectPriorityB) return projectPriorityA - projectPriorityB;
            const dateA = a.due_date ? new Date(a.due_date) : null;
            const dateB = b.due_date ? new Date(b.due_date) : null;
            if (dateA && dateB) return dateA - dateB;
            if (dateA) return -1;
            if (dateB) return 1;
            if (typeof a.id === 'string' && typeof b.id !== 'string') return 1;
            if (typeof a.id !== 'string' && typeof b.id === 'string') return -1;
            return a.id > b.id ? 1 : -1;
        });
    }, [tasks, objectiveById, projectById]);

    const { backlog, today, tomorrow, done } = useMemo(() => {
        const tenDaysAgo = new Date();
        tenDaysAgo.setDate(tenDaysAgo.getDate() - 10);
        return {
            backlog: sortedTasks.filter(t => t.planning_status === 'backlog' && !t.is_completed),
            today: sortedTasks.filter(t => t.planning_status === 'today' && !t.is_completed),
            tomorrow: sortedTasks.filter(t => t.planning_status === 'tomorrow' && !t.is_completed),
            done: sortedTasks.filter(t => t.is_completed && t.updated_at && new Date(t.updated_at) >= tenDaysAgo),
        };
    }, [sortedTasks]);

    // Etat UI mobile (déclaré avant tout return conditionnel)
    const [mobileTab, setMobileTab] = useState('today');
    const [showMobileAdd, setShowMobileAdd] = useState(false);
    const [mobileNewTask, setMobileNewTask] = useState('');
    // Calendrier mobile affiché en permanence (plus de toggle)
    const mobileTabLabel = useMemo(() => ({
        backlog: 'À Faire',
        today: "Aujourd'hui",
        tomorrow: 'Demain',
        done: 'Fait',
    }[mobileTab]), [mobileTab]);
    const targetLabelForAdd = mobileTab === 'done' ? "Aujourd'hui" : mobileTabLabel;

    const mobileList = useMemo(() => {
        switch (mobileTab) {
            case 'backlog':
                return backlog;
            case 'tomorrow':
                return tomorrow;
            case 'done':
                return done;
            case 'today':
            default:
                return today;
        }
    }, [mobileTab, backlog, today, tomorrow, done]);

    const mobileAdd = async (e) => {
        e.preventDefault();
        const value = mobileNewTask.trim();
        if (!value) return;
        const status = mobileTab === 'done' ? 'today' : mobileTab; // éviter de créer directement en 'done'
        await handleAddTask(value, status);
        setMobileNewTask('');
        setShowMobileAdd(false);
    };

    if (loading) {
        return <div className="p-4 text-center"><Loader2 className="animate-spin inline mr-2"/> Chargement du backlog...</div>;
    }
    
    if (error) {
        return <div className="p-4 text-center text-red-500">{error}</div>;
    }


    return (
        <div className="h-[calc(100vh-5rem)] flex flex-col p-4 bg-gray-200">
            <div className="flex-shrink-0 mb-4">
                <h1 className="text-2xl font-bold flex items-center"><ListChecks className="mr-2"/>Mes Tâches</h1>
            </div>

            {/* Desktop/tablette >= md: mise en page inchangée */}
            <div className="hidden md:flex flex-grow flex-col space-y-4 min-h-0">
                <div className="flex-grow flex space-x-4 min-h-0">
                    <div className="w-1/4 flex flex-col h-full">
                        <BacklogColumn title="À Faire" tasks={backlog} status="backlog" onDrop={(task) => handleDrop(task, 'backlog')} onUpdate={handleUpdateTask} onDeleteTask={handleDeleteTask} objectives={objectives} employees={allEmployees} projects={projects} onAddTask={handleAddTask} />
                    </div>
                    <div className="w-1/4 flex flex-col h-full">
                       <BacklogColumn title="Aujourd'hui" tasks={today} status="today" onDrop={(task) => handleDrop(task, 'today')} onUpdate={handleUpdateTask} onDeleteTask={handleDeleteTask} objectives={objectives} employees={allEmployees} projects={projects} bgColorClass="bg-blue-50" onAddTask={handleAddTask} />
                    </div>
                    <div className="w-1/4 flex flex-col h-full">
                       <BacklogColumn title="Demain" tasks={tomorrow} status="tomorrow" onDrop={(task) => handleDrop(task, 'tomorrow')} onUpdate={handleUpdateTask} onDeleteTask={handleDeleteTask} objectives={objectives} employees={allEmployees} projects={projects} bgColorClass="bg-yellow-50" onAddTask={handleAddTask} />
                    </div>
                    <div className="w-1/4 flex flex-col h-full">
                        <BacklogColumn title="Fait" tasks={done} status="done" onDrop={(task) => handleDrop(task, 'done')} onUpdate={handleUpdateTask} onDeleteTask={handleDeleteTask} objectives={objectives} employees={allEmployees} projects={projects} bgColorClass="bg-green-50" />
                    </div>
                </div>

                <div className="flex-shrink-0 h-1/3 bg-white rounded-lg shadow p-2">
                    <BacklogCalendarView 
                        dates={calendarDates} 
                        tasks={sortedTasks}
                        objectives={objectives}
                        projects={projects}
                        onDropOnDate={handleDropOnDate} 
                    />
                </div>
            </div>

            {/* Mobile < md: vue repensée */}
            <div className="flex md:hidden flex-col gap-3 min-h-0">
                {/* Onglets collants en haut */}
                <div className="sticky top-0 z-10 -mx-4 px-4 pt-1 pb-2 bg-gray-200">
                    <div className="grid grid-cols-4 gap-2">
                        {[
                            { key: 'backlog', label: "À Faire", count: backlog.length },
                            { key: 'today', label: "Aujourd'hui", count: today.length },
                            { key: 'tomorrow', label: "Demain", count: tomorrow.length },
                            { key: 'done', label: "Fait", count: done.length },
                        ].map(tab => (
                            <button
                                key={tab.key}
                                onClick={() => setMobileTab(tab.key)}
                                className={`text-xs px-2 py-2 rounded-full border transition-colors ${mobileTab === tab.key ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300'}`}
                            >
                                <span className="block leading-4">{tab.label}</span>
                                <span className={`text-[10px] ${mobileTab === tab.key ? 'text-blue-100' : 'text-gray-400'}`}>{tab.count}</span>
                            </button>
                        ))}
                    </div>
                    {/* Ajout rapide */}
                    <div className="mt-2">
                        {showMobileAdd ? (
                            <form onSubmit={mobileAdd} className="flex items-center gap-2">
                                <input
                                    type="text"
                                    autoFocus
                                    value={mobileNewTask}
                                    onChange={(e) => setMobileNewTask(e.target.value)}
                                    placeholder={`Ajouter dans '${targetLabelForAdd}'...`}
                                    className="flex-1 bg-white border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                <button type="button" onClick={() => { setShowMobileAdd(false); setMobileNewTask(''); }} className="text-sm text-gray-600">Annuler</button>
                                <button type="submit" className="text-sm bg-blue-600 text-white rounded px-3 py-2">Ajouter</button>
                            </form>
                        ) : (
                            <button onClick={() => setShowMobileAdd(true)} className="w-full flex items-center justify-center gap-2 text-blue-700 bg-white border border-blue-200 rounded px-3 py-2">
                                <Plus size={16}/> Ajouter une tâche
                            </button>
                        )}
                    </div>
                </div>

                {/* Liste des tâches pour l'onglet actif */}
                <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain space-y-2 pr-2" style={{ scrollbarGutter: 'stable' }}>
                    {mobileList.length > 0 ? (
                        mobileList.map(task => (
                            <BacklogTaskItem
                                key={task.id}
                                task={task}
                                onUpdate={handleUpdateTask}
                                onDelete={handleDeleteTask}
                                objectives={objectives}
                                employees={allEmployees}
                                projects={projects}
                            />
                        ))
                    ) : (
                        <div className="text-center text-gray-400 italic py-6">Aucune tâche</div>
                    )}
                </div>

                {/* Calendrier mobile toujours visible */}
                <div className="pb-2">
                    <div className="bg-white rounded-lg shadow p-2 h-72">
                        <BacklogCalendarView
                            dates={mobileCalendarDates}
                            tasks={sortedTasks}
                            objectives={objectives}
                            projects={projects}
                            onDropOnDate={handleDropOnDate}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BacklogPage;
